import React, { useState, useCallback, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { AppHeader } from '../../shared/components/AppComponents';
import { apiFetch, apiFetchJson } from '../../apiClient';
import { useTranslation } from 'react-i18next';
import CompProfileForm from './CompProfileForm';
import LevelBandsEditor from './LevelBandsEditor';
import BasePayForm from './BasePayForm';
import QuarterlyBreakdown from './QuarterlyBreakdown';
import VestingTimeline from './VestingTimeline';
import StockAccumulation from './StockAccumulation';
import StockLotsImport from './StockLotsImport';
import EsppTracker from './EsppTracker';
import StockSales from './StockSales';
import CurrencyToggle from './CurrencyToggle';
import './CompensationApp.css';

const unwrap = (data) => {
  const arr = Array.isArray(data) ? data : (data?.$values || []);
  return arr.filter(item => item && !item.$ref);
};

// The annual reward package — level, merit increase, cash bonus and stock award — is
// decided in September, so a calendar year's figures simply do not exist before then.
// 0-indexed to match Date#getMonth.
const REWARD_MONTH = 8;

// Retry wrapper for API calls that may fail due to DB cold start
const fetchWithRetry = async (fetchFn, retries = 2, delayMs = 2000) => {
  for (let i = 0; i <= retries; i++) {
    const res = await fetchFn();
    if (res && (res.ok || res.status < 500)) return res;
    if (i < retries) await new Promise(r => setTimeout(r, delayMs));
  }
  return fetchFn();
};

// Extract user-friendly error message from API response
const parseErrorResponse = async (res) => {
  try {
    const text = await res.text();
    try {
      const json = JSON.parse(text);
      return json.title || json.message || json.error || text;
    } catch { return text; }
  } catch { return `Error ${res.status}`; }
};

function CompensationApp({ user }) {
  const { instance } = useMsal();
  const { t } = useTranslation();
  const [activeView, setActiveView] = useState('overview');
  const [profiles, setProfiles] = useState([]);
  const [basePayEntries, setBasePayEntries] = useState([]);
  const [esppPurchases, setEsppPurchases] = useState([]);
  const [esppEnrollments, setEsppEnrollments] = useState([]);
  const [stockSales, setStockSales] = useState([]);
  const [quarterlyData, setQuarterlyData] = useState(null);
  const [annualSummary, setAnnualSummary] = useState(null);
  const [accumulation, setAccumulation] = useState(null);
  const [vestingTimeline, setVestingTimeline] = useState([]);
  const [stockPrice, setStockPrice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currency, setCurrency] = useState('USD');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showProjected, setShowProjected] = useState(false);
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [showBasePayForm, setShowBasePayForm] = useState(false);
  const [editingBasePay, setEditingBasePay] = useState(null);
  const [onboardingStep, setOnboardingStep] = useState(1);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [levelBands, setLevelBands] = useState([]);
  // null means "use the rating recorded on the profile"; a number is an explicit what-if.
  const [scenarioRating, setScenarioRating] = useState(null);
  const [showBandEditor, setShowBandEditor] = useState(false);

  const loadCurrentUser = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetchWithRetry(() => apiFetch(instance, '/api/Users/profile'));
      if (res.ok) {
        const data = await res.json();
        setSelectedUserId(data.id);
      } else {
        console.error('Failed to load user profile, status:', res.status);
        setError(t('compensation.errorLoadingData'));
        setLoading(false);
      }
    } catch (err) {
      console.error('Failed to load user profile:', err);
      setError(t('compensation.errorLoadingData'));
      setLoading(false);
    }
  }, [instance, t]);

  const loadProfiles = useCallback(async () => {
    if (!selectedUserId) return;
    try {
      const res = await fetchWithRetry(() => apiFetch(instance, `/api/Compensation/profiles?userId=${selectedUserId}`));
      if (res.ok) {
        const data = unwrap(await res.json());
        setProfiles(data);
      } else if (res.status >= 500) {
        setError(t('compensation.errorLoadingData'));
      }
    } catch (err) {
      console.error('Failed to load profiles:', err);
    }
  }, [instance, selectedUserId, t]);

  const loadBasePay = useCallback(async () => {
    if (!selectedUserId) return;
    try {
      const res = await fetchWithRetry(() => apiFetch(instance, `/api/BasePay?userId=${selectedUserId}`));
      if (res.ok) {
        const data = unwrap(await res.json());
        setBasePayEntries(data);
      } else if (res.status >= 500) {
        setError(t('compensation.errorLoadingData'));
      }
    } catch (err) {
      console.error('Failed to load base pay:', err);
    }
  }, [instance, selectedUserId, t]);

  const loadLevelBands = useCallback(async () => {
    if (!selectedUserId) return;
    try {
      const data = await apiFetchJson(instance, `/api/Compensation/level-bands?userId=${selectedUserId}`);
      setLevelBands(unwrap(data));
    } catch (err) {
      console.error('Failed to load level bands:', err);
    }
  }, [instance, selectedUserId]);

  const loadQuarterlyBreakdown = useCallback(async () => {
    if (!selectedUserId) return;
    try {
      const data = await apiFetchJson(instance, `/api/CompensationSummary/quarterly-breakdown?year=${selectedYear}&userId=${selectedUserId}`);
      setQuarterlyData(data);
    } catch (err) {
      console.error('Failed to load quarterly breakdown:', err);
    }
  }, [instance, selectedYear, selectedUserId]);

  const loadAnnualSummary = useCallback(async () => {
    if (!selectedUserId) return;
    try {
      const data = await apiFetchJson(instance, `/api/CompensationSummary/annual-summary?year=${selectedYear}&userId=${selectedUserId}`);
      setAnnualSummary(data);
    } catch (err) {
      console.error('Failed to load annual summary:', err);
    }
  }, [instance, selectedYear, selectedUserId]);

  const loadAccumulation = useCallback(async () => {
    if (!selectedUserId) return;
    try {
      const data = await apiFetchJson(instance, `/api/CompensationSummary/stock-accumulation?userId=${selectedUserId}`);
      setAccumulation(data);
    } catch (err) {
      console.error('Failed to load stock accumulation:', err);
    }
  }, [instance, selectedUserId]);

  const loadVestingTimeline = useCallback(async () => {
    if (!selectedUserId) return;
    try {
      const data = await apiFetchJson(instance, `/api/Compensation/vesting-timeline?userId=${selectedUserId}`);
      setVestingTimeline(unwrap(data));
    } catch (err) {
      console.error('Failed to load vesting timeline:', err);
    }
  }, [instance, selectedUserId]);

  const loadStockPrice = useCallback(async () => {
    try {
      const data = await apiFetchJson(instance, '/api/StockPrice/current/MSFT');
      setStockPrice(data);
    } catch (err) {
      console.error('Failed to load stock price:', err);
    }
  }, [instance]);

  const loadEsppPurchases = useCallback(async () => {
    if (!selectedUserId) return;
    try {
      const data = await apiFetchJson(instance, `/api/Espp/purchases?userId=${selectedUserId}`);
      setEsppPurchases(unwrap(data));
    } catch (err) {
      console.error('Failed to load ESPP purchases:', err);
    }
  }, [instance, selectedUserId]);

  const loadEsppEnrollments = useCallback(async () => {
    if (!selectedUserId) return;
    try {
      const data = await apiFetchJson(instance, `/api/Espp/enrollments?userId=${selectedUserId}`);
      setEsppEnrollments(unwrap(data));
    } catch (err) {
      console.error('Failed to load ESPP enrollments:', err);
    }
  }, [instance, selectedUserId]);

  const loadStockSales = useCallback(async () => {
    if (!selectedUserId) return;
    try {
      const data = await apiFetchJson(instance, `/api/StockSale?userId=${selectedUserId}`);
      setStockSales(unwrap(data));
    } catch (err) {
      console.error('Failed to load stock sales:', err);
    }
  }, [instance, selectedUserId]);

  const loadAllData = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      loadProfiles(),
      loadBasePay(),
      loadLevelBands(),
      loadQuarterlyBreakdown(),
      loadAnnualSummary(),
      loadAccumulation(),
      loadVestingTimeline(),
      loadStockPrice(),
      loadEsppPurchases(),
      loadEsppEnrollments(),
      loadStockSales()
    ]);
    setLoading(false);
  }, [loadProfiles, loadBasePay, loadLevelBands, loadQuarterlyBreakdown, loadAnnualSummary, loadAccumulation, loadVestingTimeline, loadStockPrice, loadEsppPurchases, loadEsppEnrollments, loadStockSales]);

  useEffect(() => {
    loadCurrentUser();
  }, [loadCurrentUser]);

  useEffect(() => {
    if (selectedUserId) {
      loadAllData();
    }
  }, [selectedUserId, selectedYear, loadAllData]);

  // Show onboarding for first-time users (no profiles and no base pay after initial load)
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  useEffect(() => {
    if (!loading && selectedUserId && !initialLoadDone) {
      setInitialLoadDone(true);
      if (profiles.length === 0 && basePayEntries.length === 0) {
        setShowOnboarding(true);
      }
    }
  }, [loading, selectedUserId, profiles, basePayEntries, initialLoadDone]);

  const handleSaveLevelBand = async (level, band) => {
    try {
      setError(null);
      const res = await fetchWithRetry(() => apiFetch(instance, `/api/Compensation/level-bands/${level}`, {
        method: 'PUT',
        body: JSON.stringify(band)
      }));
      if (!res.ok) { setError(await parseErrorResponse(res)); return; }
      await loadLevelBands();
    } catch (err) {
      console.error('Failed to save level band:', err);
      setError(err.message);
    }
  };

  const handleResetLevelBand = async (level) => {
    try {
      setError(null);
      const res = await fetchWithRetry(() => apiFetch(instance, `/api/Compensation/level-bands/${level}`, {
        method: 'DELETE'
      }));
      // A 404 just means there was no override to remove, which is the desired end state.
      if (!res.ok && res.status !== 404) { setError(await parseErrorResponse(res)); return; }
      await loadLevelBands();
    } catch (err) {
      console.error('Failed to reset level band:', err);
      setError(err.message);
    }
  };

  const handleSaveProfile = async (profileData) => {
    try {
      setError(null);
      let res;
      if (editingProfile) {
        res = await fetchWithRetry(() => apiFetch(instance, `/api/Compensation/profiles/${editingProfile.id}`, {
          method: 'PUT',
          body: JSON.stringify(profileData)
        }));
      } else {
        res = await fetchWithRetry(() => apiFetch(instance, '/api/Compensation/profiles', {
          method: 'POST',
          body: JSON.stringify(profileData)
        }));
      }
      if (res.ok) {
        setShowProfileForm(false);
        setEditingProfile(null);
        loadAllData();
      } else {
        const msg = await parseErrorResponse(res);
        setError(msg || t('compensation.errorSaving'));
      }
    } catch (err) {
      console.error('Failed to save profile:', err);
      setError(t('compensation.errorSaving'));
    }
  };

  const handleDeleteProfile = async (profileId) => {
    if (!window.confirm(t('compensation.deleteConfirm'))) return;
    try {
      setError(null);
      await apiFetchJson(instance, `/api/Compensation/profiles/${profileId}`, { method: 'DELETE' });
      loadAllData();
    } catch (err) {
      console.error('Failed to delete profile:', err);
      setError(t('compensation.errorDeleting'));
    }
  };

  const handleSaveBasePay = async (data) => {
    try {
      setError(null);
      const url = editingBasePay
        ? `/api/BasePay/${editingBasePay.id}`
        : '/api/BasePay';
      const method = editingBasePay ? 'PUT' : 'POST';
      const res = await fetchWithRetry(() => apiFetch(instance, url, {
        method,
        body: JSON.stringify(data)
      }));
      if (res.ok) {
        setShowBasePayForm(false);
        setEditingBasePay(null);
        setActiveView('profiles');
        loadBasePay();
        loadAllData();
      } else {
        const msg = await parseErrorResponse(res);
        setError(msg || t('compensation.errorSaving'));
      }
    } catch (err) {
      console.error('Failed to save base pay:', err);
      setError(t('compensation.errorSaving'));
    }
  };

  const handleDeleteBasePay = async (id) => {
    if (!window.confirm(t('compensation.deleteBasePayConfirm'))) return;
    try {
      await apiFetchJson(instance, `/api/BasePay/${id}`, { method: 'DELETE' });
      loadBasePay();
      loadAllData();
    } catch (err) {
      console.error('Failed to delete base pay:', err);
      setError(t('compensation.errorDeleting'));
    }
  };

  const formatCurrency = (amountUSD, exchangeRate) => {
    if (amountUSD == null) return '—';
    if (currency === 'NOK' && exchangeRate) {
      return `${(amountUSD * exchangeRate).toLocaleString('nb-NO', { maximumFractionDigits: 0 })} kr`;
    }
    return `$${amountUSD.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  };

  const exchangeRate = stockPrice?.exchangeRate || annualSummary?.exchangeRateUSDNOK;

  const views = [
    { id: 'overview', label: t('compensation.overview'), icon: '📊' },
    { id: 'vesting', label: t('compensation.vesting'), icon: '📈' },
    { id: 'accumulation', label: t('compensation.accumulation'), icon: '💎' },
    { id: 'sales', label: t('compensation.sales'), icon: '💰' },
    { id: 'espp', label: t('compensation.espp'), icon: '🛒' },
    { id: 'profiles', label: t('compensation.compData'), icon: '📋' }
  ];

  const hasCompData = !loading && !showOnboarding && (profiles.length > 0 || basePayEntries.length > 0);

  const currentBasePay = [...basePayEntries]
    .sort((a, b) => new Date(b.effectiveDate) - new Date(a.effectiveDate))[0];

  return (
    <div className="compensation-app">
      <AppHeader
        title={t('compensation.title')}
        user={user}
        actions={hasCompData ? (
          <>
            {stockPrice && (
              <div className="stock-chip">
                <span className="stock-chip-symbol">MSFT</span>
                <span className="stock-chip-price">${stockPrice.priceUSD?.toFixed(2)}</span>
                {stockPrice.priceNOK && (
                  <span className="stock-chip-alt">{stockPrice.priceNOK?.toFixed(2)} kr</span>
                )}
              </div>
            )}
            <CurrencyToggle currency={currency} onChange={setCurrency} />
          </>
        ) : null}
      />

      {error && (
        <div className="comp-error" role="alert">
          <span>{error}</span>
          <button onClick={() => setError(null)} aria-label="Dismiss error">✕</button>
        </div>
      )}

      {loading && (
        <div className="comp-loading">
          <div className="loading-spinner" />
          <p>{t('common.loading')}</p>
        </div>
      )}

      {/* First-run onboarding wizard */}
      {!loading && showOnboarding && (
        <div className="comp-onboarding">
          <div className="onboarding-hero">
            <span className="onboarding-icon">💰</span>
            <h2>{t('compensation.welcomeTitle')}</h2>
            <p>{t('compensation.welcomeDescription')}</p>
            <p className="onboarding-reassurance">{t('compensation.onboardingReassurance')}</p>
          </div>

          <div className="onboarding-steps">
            <span className={`step-dot ${onboardingStep >= 1 ? 'active' : ''}`}>1</span>
            <span className="step-line" />
            <span className={`step-dot ${onboardingStep >= 2 ? 'active' : ''}`}>2</span>
          </div>

          {onboardingStep === 1 && (
            <>
              <h3>{t('compensation.step1BasePay')}</h3>
              <p className="onboarding-step-desc">{t('compensation.step1Description')}</p>
              <BasePayForm
                entry={null}
                onSave={async (data) => {
                  await handleSaveBasePay(data);
                  setOnboardingStep(2);
                }}
                onCancel={() => {}}
              />
            </>
          )}

          {onboardingStep === 2 && (
            <>
              <h3>{t('compensation.step2SignOn')}</h3>
              <p className="onboarding-step-desc">{t('compensation.step2Description')}</p>
              <CompProfileForm
                profile={null}
                onSave={async (data) => {
                  await handleSaveProfile(data);
                  setShowOnboarding(false);
                  setActiveView('profiles');
                }}
                onCancel={() => {
                  setShowOnboarding(false);
                  setActiveView('profiles');
                  loadAllData();
                }}
                isOnboarding={true}
              />
              <button className="btn-text skip-btn" onClick={() => { setShowOnboarding(false); setActiveView('profiles'); loadAllData(); }}>
                {t('compensation.skipForNow')} →
              </button>
            </>
          )}
        </div>
      )}

      {/* Main app — shown when onboarding is complete */}
      {hasCompData && (
        <>
          {/* View tabs */}
          <div className="comp-tabs" role="tablist" aria-label="Compensation views">
            {views.map(v => (
              <button
                key={v.id}
                className={`comp-tab ${activeView === v.id ? 'active' : ''}`}
                onClick={() => setActiveView(v.id)}
                role="tab"
                aria-selected={activeView === v.id}
              >
                <span className="tab-icon">{v.icon}</span>
                <span className="tab-label">{v.label}</span>
              </button>
            ))}
          </div>

          {/* View content */}
          <div className="comp-content">
            {activeView === 'overview' && (
              <div className="overview-view">
                <div className="overview-controls">
                  <div className="year-selector">
                    <button onClick={() => setSelectedYear(y => y - 1)} aria-label="Previous year">◀</button>
                    <span>{selectedYear}</span>
                    <button onClick={() => setSelectedYear(y => y + 1)} aria-label="Next year">▶</button>
                  </div>
                  {selectedYear === new Date().getFullYear() && (
                    <button
                      className={`projected-toggle ${showProjected ? 'active' : ''}`}
                      onClick={() => setShowProjected(p => !p)}
                    >
                      {showProjected ? `📈 ${t('compensation.projected')}` : `📅 ${t('compensation.actualSoFar')}`}
                    </button>
                  )}
                </div>

                {annualSummary && (() => {
                  const isCurrentYear = selectedYear === new Date().getFullYear();
                  const now = new Date();
                  const monthsElapsed = isCurrentYear ? now.getMonth() + (now.getDate() / 30) : 12;
                  const useProjected = !isCurrentYear || showProjected;

                  // Base salary: prorate for actual
                  const baseSalaryNOK = useProjected
                    ? annualSummary.baseSalaryNOK
                    : annualSummary.baseSalaryNOK * (monthsElapsed / 12);

                  // Cash bonus: for actual, only count if paid date has passed
                  const cashBonusNOK = useProjected
                    ? (annualSummary.cashBonusNOK || 0)
                    : (profiles.find(p => p.compYear === selectedYear && p.cashBonusPaidDate && new Date(p.cashBonusPaidDate) <= now)
                        ? (annualSummary.cashBonusNOK || 0) : 0);

                  // The level in force for the year decides which reward range applies.
                  const yearProfile = profiles.find(p => p.compYear === selectedYear);
                  const missingProfile = annualSummary.hasProfileForYear === false;
                  const yearEnd = new Date(selectedYear, 11, 31);
                  const levelEntry = [...basePayEntries]
                    .filter(bp => bp.level && new Date(bp.effectiveDate) <= yearEnd)
                    .sort((a, b) => new Date(b.effectiveDate) - new Date(a.effectiveDate))[0];
                  const yearLevel = levelEntry?.level ?? null;
                  const band = yearLevel ? levelBands.find(b => b.level === yearLevel) : null;

                  // The reward scale runs 0-200 and an on-target 100 pays half the range, so
                  // on-target is simply half the maximum and every rating scales linearly
                  // from there: base × onTarget% × rating/100.
                  const profileRating = yearProfile?.performanceMultiplier ?? 100;
                  const rating = scenarioRating ?? profileRating;
                  const cashMaxPct = band?.cashBonusMaxPercent ?? null;
                  const stockMaxUSD = band?.stockAwardMaxUSD ?? null;
                  const onTargetCashPct = cashMaxPct != null ? cashMaxPct / 2 : null;
                  const onTargetStockUSD = stockMaxUSD != null ? stockMaxUSD / 2 : null;
                  const scenarioCashPct = onTargetCashPct != null ? onTargetCashPct * (rating / 100) : null;
                  const scenarioCashNOK = scenarioCashPct != null
                    ? annualSummary.baseSalaryNOK * (scenarioCashPct / 100)
                    : null;
                  const scenarioStockUSD = onTargetStockUSD != null ? onTargetStockUSD * (rating / 100) : null;
                  const scenarioStockNOK = scenarioStockUSD != null && exchangeRate
                    ? scenarioStockUSD * exchangeRate
                    : null;

                  // A grant starts vesting the same year it is awarded — an autumn award
                  // first vests that November — but with no profile for the year yet there
                  // are no vesting events to show it. Project that first slice from the
                  // vesting convention on the most recent profile, otherwise the year total
                  // is quietly short by it.
                  const referenceProfile = [...profiles].sort((a, b) => b.compYear - a.compYear)[0];
                  const vestQuarters = referenceProfile?.vestingPeriodQuarters ?? 20;
                  const vestAnchorMonth = referenceProfile?.vestingStartDate
                    ? new Date(referenceProfile.vestingStartDate).getMonth()
                    : 10;
                  const projectedGrantQuarters = (useProjected && missingProfile && scenarioStockUSD != null)
                    ? Array.from({ length: vestQuarters }, (_, i) =>
                        new Date(selectedYear, vestAnchorMonth + i * 3 + 1, 0)
                      ).filter(d => d.getFullYear() === selectedYear).length
                    : 0;
                  const projectedGrantVestUSD = projectedGrantQuarters > 0
                    ? (scenarioStockUSD / vestQuarters) * projectedGrantQuarters
                    : 0;

                  // Once the bonus has actually been paid the recorded figure is the truth.
                  // Until then the range predicts it better than a recorded zero does.
                  const bonusPaid = !!(yearProfile?.cashBonusPaidDate
                    && new Date(yearProfile.cashBonusPaidDate) <= now);
                  const usingScenarioBonus = useProjected && scenarioCashNOK != null && !bonusPaid;
                  const effectiveCashBonusNOK = usingScenarioBonus ? scenarioCashNOK : cashBonusNOK;

                  // A different rating changes this year's grant, which changes the shares it
                  // vests. Only this year's grant scales, and only vests still to come — ones
                  // that already happened are facts. Sign-on grants are excluded because they
                  // are negotiated up front and never scaled by a performance rating.
                  const stockScenarioRatio = profileRating > 0 ? rating / profileRating : 1;
                  const sharesOf = (e) => {
                    const shares = e.sharesVested || 0;
                    const scales = e.compYear === selectedYear
                      && !e.isSignOnBonus
                      && new Date(e.vestDate) > now;
                    return scales ? shares * stockScenarioRatio : shares;
                  };

                  // Stock vested: for actual, only count events with vestDate <= now
                  const yearVestEvents = vestingTimeline.filter(e =>
                    new Date(e.vestDate).getFullYear() === selectedYear
                  );
                  const actualVestEvents = yearVestEvents.filter(e => new Date(e.vestDate) <= now);
                  const actualVestedShares = actualVestEvents
                    .reduce((sum, e) => sum + (e.sharesVested || 0), 0);
                  const projectedVestedShares = yearVestEvents
                    .reduce((sum, e) => sum + sharesOf(e), 0);
                  const vestedShares = useProjected ? projectedVestedShares : actualVestedShares;

                  const stockPriceUSD = stockPrice?.priceUSD || annualSummary.currentStockPriceUSD || 0;
                  // Already-vested shares are worth what they were worth on vest day, not today.
                  // Projections have no vest price yet, so they fall back to the current price.
                  const vestedValueUSD = useProjected
                    ? vestedShares * stockPriceUSD
                    : actualVestEvents.reduce(
                        (sum, e) => sum + (e.sharesVested || 0) * (e.stockPriceAtVest || stockPriceUSD),
                        0
                      );

                  // How much of this year's own grant lands inside this year. This is the
                  // slice of the award the scenario actually moves in the total above — the
                  // rest of it vests in later years.
                  const thisYearGrantShares = yearVestEvents
                    .filter(e => e.compYear === selectedYear && !e.isSignOnBonus)
                    .reduce((sum, e) => sum + sharesOf(e), 0);
                  const thisYearGrantVestNOK = exchangeRate
                    ? (thisYearGrantShares * stockPriceUSD + projectedGrantVestUSD) * exchangeRate
                    : 0;

                  // The ESPP discount is real compensation — you receive shares worth more
                  // than you paid — and it was being left out of the total entirely.
                  // Recorded purchases are always in the past, so this is the same figure
                  // in both actual and projected mode.
                  const esppDiscountUSD = annualSummary.esppDiscountGainUSD || 0;
                  const esppDiscountNOK = exchangeRate ? esppDiscountUSD * exchangeRate : 0;

                  // Total comp in NOK
                  const stockValueNOK = exchangeRate ? (vestedValueUSD + projectedGrantVestUSD) * exchangeRate : 0;
                  const totalCompNOK = baseSalaryNOK + effectiveCashBonusNOK + stockValueNOK + esppDiscountNOK;

                  // The reward package — level, merit increase, cash bonus and stock award —
                  // is only decided in September, so before then the year's figures cannot
                  // exist yet and asking for them is noise. Until the package lands we say
                  // so and lean on the level band + scenario instead of nagging.
                  const currentYear = new Date().getFullYear();
                  const rewardDecided = selectedYear < currentYear
                    || (isCurrentYear && now.getMonth() >= REWARD_MONTH);
                  const bonusPending = useProjected
                    && isCurrentYear
                    && cashBonusNOK > 0
                    && annualSummary.cashBonusPaidDate
                    && new Date(annualSummary.cashBonusPaidDate) > now;

                  // Bonus, vesting income and the ESPP discount all land on top of base
                  // salary, so they are taxed at the marginal rate rather than the average
                  // one. Base salary is deliberately excluded — taxing it at the marginal
                  // rate would badly overstate the bill.
                  const yearProfileTaxRate = yearProfile?.marginalTaxRatePercent;
                  const taxRate = yearProfileTaxRate;
                  const hasTaxRate = taxRate !== null && taxRate !== undefined && taxRate !== '';
                  const variableCompNOK = effectiveCashBonusNOK + stockValueNOK + esppDiscountNOK;
                  const estimatedTaxNOK = hasTaxRate ? variableCompNOK * (taxRate / 100) : 0;

                  // The next vest is a portfolio-wide question, so it deliberately ignores
                  // the selected year — otherwise viewing an old year would hide it.
                  const nextVest = vestingTimeline
                    .filter(e => new Date(e.vestDate) > now)
                    .sort((a, b) => new Date(a.vestDate) - new Date(b.vestDate))[0];
                  const nextVestDays = nextVest
                    ? Math.ceil((new Date(nextVest.vestDate) - now) / 86400000)
                    : null;
                  const nextVestGrossNOK = nextVest && exchangeRate
                    ? (nextVest.sharesVested || 0) * stockPriceUSD * exchangeRate
                    : 0;

                  const fmtNOK = (v) => `${Math.round(v).toLocaleString('nb-NO')} kr`;
                  const fmtVal = (nok) => currency === 'NOK'
                    ? fmtNOK(nok)
                    : (exchangeRate ? `$${Math.round(nok / exchangeRate).toLocaleString('en-US')}` : '—');

                  return (
                    <>
                      {/* Total comp hero */}
                      <div className="total-comp-card">
                        <div className="total-comp-label">
                          {t('compensation.totalComp')} {selectedYear}
                          {isCurrentYear && !useProjected && (
                            <span className="actual-tag">{t('compensation.soFar')}</span>
                          )}
                        </div>
                        <div className="total-comp-value">{fmtVal(totalCompNOK)}</div>
                        <div className="total-comp-breakdown">
                          <span>🏦 {fmtVal(baseSalaryNOK)}</span>
                          <span>🎁 {fmtVal(effectiveCashBonusNOK)}</span>
                          <span>📈 {fmtVal(stockValueNOK)}</span>
                          {esppDiscountNOK > 0 && <span>🛒 {fmtVal(esppDiscountNOK)}</span>}
                        </div>
                        {missingProfile && (rewardDecided ? (
                          <div className="comp-note warn">
                            {t('compensation.noProfileForYear', { year: selectedYear })}
                            {' '}
                            <button
                              type="button"
                              className="inline-link"
                              onClick={() => {
                                setActiveView('profiles');
                                setEditingProfile(null);
                                setShowProfileForm(true);
                              }}
                            >
                              {t('compensation.addProfileNow', { year: selectedYear })}
                            </button>
                          </div>
                        ) : (
                          <div className="comp-note">
                            {t('compensation.rewardNotDecidedYet', { year: selectedYear })}
                          </div>
                        ))}
                        {bonusPending && (
                          <div className="comp-note">
                            {t('compensation.bonusPending', {
                              date: new Date(annualSummary.cashBonusPaidDate)
                                .toLocaleDateString('nb-NO', { month: 'long' })
                            })}
                          </div>
                        )}
                        {hasTaxRate && variableCompNOK > 0 && (
                          <div className="comp-tax-line">
                            <span>
                              {t('compensation.estimatedTax', { rate: taxRate })}
                              {' '}−{fmtVal(estimatedTaxNOK)}
                            </span>
                            <strong>
                              {t('compensation.netVariable')} {fmtVal(variableCompNOK - estimatedTaxNOK)}
                            </strong>
                          </div>
                        )}
                      </div>

                      {yearLevel && (
                        <div className="reward-range-card">
                          <div className="reward-range-head">
                            <span className="reward-range-label">
                              {t('compensation.rewardRange')}
                              <span className="level-badge">L{yearLevel}</span>
                            </span>
                            <div className="scenario-picker" role="group" aria-label={t('compensation.scenario')}>
                              {[70, 100, 140].map(r => (
                                <button
                                  key={r}
                                  type="button"
                                  className={`scenario-btn ${rating === r ? 'active' : ''}`}
                                  onClick={() => setScenarioRating(r)}
                                  aria-pressed={rating === r}
                                >
                                  {r}%
                                </button>
                              ))}
                              {scenarioRating !== null && scenarioRating !== profileRating && (
                                <button
                                  type="button"
                                  className="scenario-reset"
                                  onClick={() => setScenarioRating(null)}
                                >
                                  {t('compensation.useRecorded', { rating: profileRating })}
                                </button>
                              )}
                            </div>
                          </div>

                          {!band && (
                            <p className="comp-note warn">
                              {t('compensation.noBandForLevel', { level: yearLevel })}
                            </p>
                          )}

                          {cashMaxPct != null && (
                            <div className="range-row">
                              <div className="range-row-head">
                                <span className="range-row-label">{t('compensation.cashBonus')}</span>
                                <span className="range-row-range">
                                  0–{cashMaxPct}% {t('compensation.ofBase')}
                                </span>
                              </div>
                              <div className="range-bar">
                                <div className="range-bar-fill" style={{ width: `${Math.min(100, rating / 2)}%` }} />
                                <span className="range-bar-tick" style={{ left: '50%' }} />
                              </div>
                              <div className="range-row-foot">
                                <strong>{scenarioCashPct.toFixed(1)}% · {fmtVal(scenarioCashNOK)}</strong>
                                <span>{t('compensation.onTargetIs', { pct: onTargetCashPct })}</span>
                              </div>
                            </div>
                          )}

                          {stockMaxUSD != null ? (
                            <div className="range-row">
                              <div className="range-row-head">
                                <span className="range-row-label">{t('compensation.stockAward')}</span>
                                <span className="range-row-range">
                                  $0–${stockMaxUSD.toLocaleString('en-US')}
                                </span>
                              </div>
                              <div className="range-bar">
                                <div className="range-bar-fill stock" style={{ width: `${Math.min(100, rating / 2)}%` }} />
                                <span className="range-bar-tick" style={{ left: '50%' }} />
                              </div>
                              <div className="range-row-foot">
                                <strong>
                                  ${Math.round(scenarioStockUSD).toLocaleString('en-US')}
                                  {scenarioStockNOK != null && currency === 'NOK' && ` · ${fmtNOK(scenarioStockNOK)}`}
                                </strong>
                                <span>
                                  {t('compensation.vestsOverYears', {
                                    years: Math.round((yearProfile?.vestingPeriodQuarters ?? 20) / 4)
                                  })}
                                </span>
                              </div>
                              {(thisYearGrantShares > 0 || projectedGrantVestUSD > 0) ? (
                                <div className="range-row-subfoot">
                                  {t('compensation.vestsInYear', { year: selectedYear })}
                                  {' '}{fmtVal(thisYearGrantVestNOK)}
                                  {thisYearGrantShares > 0
                                    ? `${' · '}${thisYearGrantShares.toFixed(2)} ${t('compensation.shares')}`
                                    : `${' · '}${t('compensation.quartersOfGrant', { count: projectedGrantQuarters })}`}
                                </div>
                              ) : (
                                <div className="range-row-subfoot">
                                  {t('compensation.noneVestsThisYear', { year: selectedYear })}
                                </div>
                              )}
                            </div>
                          ) : band && (
                            <p className="comp-note">{t('compensation.noStockRange', { level: yearLevel })}</p>
                          )}

                          {/* The award is a grant that vests over five years, so counting it
                              whole would double-count against the vesting already in the total. */}
                          <p className="reward-range-foot">{t('compensation.grantedNotVested')}</p>
                        </div>
                      )}

                      {nextVest && (
                        <div className="next-vest-card">
                          <div className="next-vest-head">
                            <span className="next-vest-label">{t('compensation.nextVest')}</span>
                            <span className="next-vest-countdown">
                              {nextVestDays <= 0
                                ? t('compensation.vestingToday')
                                : t('compensation.inDays', { count: nextVestDays })}
                            </span>
                          </div>
                          <div className="next-vest-body">
                            <div className="next-vest-date">
                              {new Date(nextVest.vestDate).toLocaleDateString('nb-NO', {
                                day: 'numeric', month: 'long', year: 'numeric'
                              })}
                            </div>
                            <div className="next-vest-figures">
                              <span>{(nextVest.sharesVested || 0).toFixed(2)} {t('compensation.shares')}</span>
                              <span>≈ {fmtVal(nextVestGrossNOK)}</span>
                              {hasTaxRate && (
                                <span className="next-vest-net">
                                  {t('compensation.afterTax')} {fmtVal(nextVestGrossNOK * (1 - taxRate / 100))}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Detail cards */}
                      <div className="summary-cards">
                        <div className="summary-card">
                          <div className="card-label">{t('compensation.baseSalary')}</div>
                          <div className="card-value">{fmtVal(baseSalaryNOK)}</div>
                        </div>
                        <div className="summary-card">
                          <div className="card-label">{t('compensation.cashBonus')}</div>
                          <div className="card-value">{fmtVal(effectiveCashBonusNOK)}</div>
                        </div>
                        <div className="summary-card">
                          <div className="card-label">{t('compensation.stockVested')}</div>
                          <div className="card-value">
                            {vestedShares.toFixed(2)} {t('compensation.shares')}
                          </div>
                          <div className="card-sub">{fmtVal(stockValueNOK)}</div>
                        </div>
                      </div>
                    </>
                  );
                })()}

                {/* Monthly breakdown chart */}
                <QuarterlyBreakdown
                  data={quarterlyData}
                  currency={currency}
                  exchangeRate={exchangeRate}
                  stockPrice={stockPrice?.priceUSD}
                />
              </div>
            )}

            {activeView === 'vesting' && (
              <VestingTimeline
                events={vestingTimeline}
                currency={currency}
                exchangeRate={exchangeRate}
                currentPrice={stockPrice?.priceUSD}
              />
            )}

            {activeView === 'accumulation' && (
              <>
                <StockAccumulation
                  data={accumulation}
                  currency={currency}
                  exchangeRate={exchangeRate}
                />
                <StockLotsImport
                  instance={instance}
                  selectedUserId={selectedUserId}
                  onImported={() => { loadEsppPurchases(); loadAccumulation(); loadVestingTimeline(); }}
                />
              </>
            )}

            {activeView === 'sales' && (
              <StockSales
                sales={stockSales}
                currency={currency}
                exchangeRate={exchangeRate}
                currentPrice={stockPrice?.priceUSD}
                instance={instance}
                selectedUserId={selectedUserId}
                onRefresh={() => { loadStockSales(); loadAccumulation(); }}
              />
            )}

            {activeView === 'espp' && (
              <EsppTracker
                purchases={esppPurchases}
                enrollments={esppEnrollments}
                currency={currency}
                exchangeRate={exchangeRate}
                currentPrice={stockPrice?.priceUSD}
                instance={instance}
                onRefresh={() => { loadEsppPurchases(); loadEsppEnrollments(); }}
              />
            )}

            {activeView === 'profiles' && (
              <div className="profiles-view">
                {/* Base Pay History */}
                <div className="comp-list-section">
                  <div className="section-head">
                    <div className="section-head-text">
                      <h2>{t('compensation.basePayHistory')}</h2>
                      <p className="section-head-meta">{t('compensation.basePayHistoryDesc')}</p>
                    </div>
                    <div className="section-head-side">
                      {currentBasePay?.annualBaseSalaryNOK != null && (
                        <div className="section-stat">
                          <span className="section-stat-label">{t('compensation.currentBase')}</span>
                          <span className="section-stat-value">
                            {currentBasePay.annualBaseSalaryNOK.toLocaleString('nb-NO')} kr
                          </span>
                        </div>
                      )}
                      <button className="btn-primary" onClick={() => { setEditingBasePay(null); setShowBasePayForm(true); }}>
                        + {t('compensation.addBasePay')}
                      </button>
                    </div>
                  </div>

                  {showBasePayForm && (
                    <div className="basepay-form-container">
                      <BasePayForm
                        entry={editingBasePay}
                        onSave={handleSaveBasePay}
                        onCancel={() => { setShowBasePayForm(false); setEditingBasePay(null); }}
                      />
                    </div>
                  )}

                  <div className="comp-list-table">
                    <table className="comp-table">
                      <thead>
                        <tr>
                          <th>{t('compensation.startDate')}</th>
                          <th>{t('compensation.endDate')}</th>
                          <th>{t('compensation.level')}</th>
                          <th>{t('compensation.baseSalary')} (NOK)</th>
                          <th>{t('compensation.notes')}</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const sorted = [...basePayEntries].sort((a, b) => new Date(b.effectiveDate) - new Date(a.effectiveDate));
                          return sorted.map((bp, idx) => {
                            const newerEntry = sorted[idx - 1];
                            const endDate = newerEntry
                              ? new Date(new Date(newerEntry.effectiveDate).getTime() - 86400000).toLocaleDateString('nb-NO')
                              : null;
                            return (
                              <tr key={bp.id} className={idx === 0 ? 'current-row' : ''}>
                                <td>{new Date(bp.effectiveDate).toLocaleDateString('nb-NO')}</td>
                                <td>{endDate || <span className="current-badge">{t('compensation.present')}</span>}</td>
                                <td>{bp.level ? <span className="level-badge">L{bp.level}</span> : '—'}</td>
                                <td className="amount-cell">{bp.annualBaseSalaryNOK?.toLocaleString('nb-NO')} kr</td>
                                <td className="notes-cell">{bp.notes || ''}</td>
                                <td className="actions-cell">
                                  <button className="btn-icon" onClick={() => { setEditingBasePay(bp); setShowBasePayForm(true); }} aria-label="Edit base pay entry">✏️</button>
                                  <button className="btn-icon" onClick={() => handleDeleteBasePay(bp.id)} aria-label="Delete base pay entry">🗑️</button>
                                </td>
                              </tr>
                            );
                          });
                        })()}
                        {basePayEntries.length === 0 && (
                          <tr><td colSpan="6" className="empty-cell">{t('compensation.noBasePay')}</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Level reward ranges — what each level can pay, used to project scenarios */}
                <div className="comp-list-section">
                  <div className="section-head">
                    <div className="section-head-text">
                      <h2>{t('compensation.levelRanges')}</h2>
                      <p className="section-head-meta">{t('compensation.levelRangesDesc')}</p>
                    </div>
                    <div className="section-head-side">
                      <button className="btn-text" onClick={() => setShowBandEditor(s => !s)}>
                        {showBandEditor ? '▲' : '▼'}
                      </button>
                    </div>
                  </div>
                  {showBandEditor && (
                    <LevelBandsEditor
                      bands={levelBands}
                      onSave={handleSaveLevelBand}
                      onReset={handleResetLevelBand}
                    />
                  )}
                </div>

                {/* Annual Compensation (cash bonus + stock awards per year) */}
                <div className="comp-list-section">
                  <div className="section-head">
                    <div className="section-head-text">
                      <h2>{t('compensation.annualComp')}</h2>
                      <p className="section-head-meta">{t('compensation.annualCompDesc')}</p>
                    </div>
                    <div className="section-head-side">
                      {profiles.length > 0 && (
                        <div className="section-stat">
                          <span className="section-stat-label">{t('compensation.yearsTracked')}</span>
                          <span className="section-stat-value">{profiles.length}</span>
                        </div>
                      )}
                      <button className="btn-primary" onClick={() => { setEditingProfile(null); setShowProfileForm(true); }}>
                        + {t('compensation.addCompYear')}
                      </button>
                    </div>
                  </div>

                  {showProfileForm && (
                    <CompProfileForm
                      profile={editingProfile}
                      onSave={handleSaveProfile}
                      onCancel={() => { setShowProfileForm(false); setEditingProfile(null); }}
                    />
                  )}

                  <div className="comp-year-bento">
                  {[...profiles].sort((a, b) => b.compYear - a.compYear).map((p, idx) => (
                    <div key={p.id} className={`comp-year-card${idx === 0 ? ' is-latest' : ''}`}>
                      <div className="comp-year-header">
                        <h3>{p.compYear}</h3>
                        <div className="comp-year-actions">
                          <button className="btn-icon" onClick={() => { setEditingProfile(p); setShowProfileForm(true); }} aria-label="Edit compensation year">✏️</button>
                          <button className="btn-icon" onClick={() => handleDeleteProfile(p.id)} aria-label="Delete compensation year">🗑️</button>
                        </div>
                      </div>
                      <div className="comp-year-grid">
                        <div className="comp-year-item" data-kind="cash">
                          <span className="comp-item-label">{t('compensation.cashBonus')}</span>
                          <span className="comp-item-value">
                            {p.cashBonusNOK ? `${p.cashBonusNOK.toLocaleString('nb-NO')} kr` : '—'}
                          </span>
                          {p.cashBonusPaidDate && (
                            <span className="comp-item-sub">{new Date(p.cashBonusPaidDate).toLocaleDateString('nb-NO')}</span>
                          )}
                        </div>
                        {p.stockGrantTotalUSD > 0 && (
                          <div className="comp-year-item" data-kind="stock">
                            <span className="comp-item-label">{t('compensation.annualGrant')}</span>
                            <span className="comp-item-value">${p.stockGrantTotalUSD?.toLocaleString('en-US')}</span>
                            <span className="comp-item-sub">
                              {p.sharesGranted?.toFixed(2)} {t('compensation.shares')} @ ${p.stockPriceAtGrant?.toFixed(2)}
                            </span>
                            <span className="comp-item-sub">
                              {t('compensation.vestingStart')}: {p.vestingStartDate ? new Date(p.vestingStartDate).toLocaleDateString('nb-NO') : '—'} · {p.vestingPeriodQuarters ? `${p.vestingPeriodQuarters / 4} yr` : '5 yr'}
                            </span>
                          </div>
                        )}
                        {p.signOnBonusUSD > 0 && (
                          <div className="comp-year-item" data-kind="signon">
                            <span className="comp-item-label">{t('compensation.signOn')}</span>
                            <span className="comp-item-value">${p.signOnBonusUSD?.toLocaleString('en-US')}</span>
                            <span className="comp-item-sub">
                              {p.signOnBonusShares?.toFixed(2)} {t('compensation.shares')} @ ${p.signOnBonusPriceAtGrant?.toFixed(2)}
                            </span>
                            <span className="comp-item-sub">
                              {t('compensation.vestingStart')}: {p.signOnVestStartDate ? new Date(p.signOnVestStartDate).toLocaleDateString('nb-NO') : '—'} · {p.signOnVestQuarters ? `${p.signOnVestQuarters / 4} yr` : '—'}
                            </span>
                          </div>
                        )}
                      </div>
                      {p.notes && <div className="comp-year-notes">{p.notes}</div>}
                    </div>
                  ))}
                  </div>
                  {profiles.length === 0 && (
                    <div className="empty-state">{t('compensation.noProfiles')}</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default CompensationApp;
